import {Injectable} from '@angular/core';
import {Observable, of, Subject} from 'rxjs';
import {catchError, filter, map, switchMap, take, tap} from 'rxjs/operators';
import {UiControl} from '../models/uiControl';
import {EventService} from './event.service';
import {ErrorService, MomentService, UiConfigService, RuntimeContext, KeycloakLv1871Service} from '@lv1871/core';
import {UiVorgang} from '../models/uiVorgang';
import {ShellConfigService} from './shell-config.service';
import {Router} from '@angular/router';
import {
  DateControl,
  flatMap,
  syncSameArtifacts,
  UIArtefakt,
  UiBaseControl} from '../models/uiArtefakt';
import {UIDarstellungsart} from '../models/uiDarstellungsart';
import {UILayout} from '../models/uiLayout';
import {UIArtefaktTyp} from '../models/uiArtefaktTyp';
import {VermittlerdatenService} from './vermittlerdaten.service';
import {AntragsdatenserviceRestService} from './rest/antragsdatenservice-rest.service';
import {CalculationService} from './calculation.service';
import {AntragsannahmeUrlParams} from '../models/urlParams';

const createRequestFailureUiControl = (message: string): UiControl => ({
  uiVorgang: {
    UIArtefakt: [],
    UIMeldung: [{
      Meldungstext: message,
      Meldungskategorie: 'Fehler'
    }]
  } as UiVorgang
} as UiControl);

@Injectable()
export class UiModelService {
  private _uiControlRequests$ = new Subject<any>();
  private _secondInitialRequest: boolean = false;


  constructor(private _eventService: EventService,
              private _antragsdatenRestService: AntragsdatenserviceRestService,
              private _shellConfigService: ShellConfigService,
              private _uiConfigService: UiConfigService, private _errorService: ErrorService,
              private _router: Router, private _momentService: MomentService, private _authService: KeycloakLv1871Service,
              private _vermittlerdatenService: VermittlerdatenService, private _calculationService: CalculationService) {

    this._uiControlRequests$.pipe(
        tap((uiControl: UiControl) => {
          if (!uiControl?.uiVorgang?.UIArtefakt) {
            this._secondInitialRequest = true;
          }
        }),
        switchMap((body: UiControl) => {
          this._eventService.uiControlsRequestPending$.next(true);
          this._calculationService.asyncBerechnungSubject$.next(body);
          return this._antragsdatenRestService.postUiControl(body).pipe(
              filter(response => !!response),
              tap((uiControl: UiControl) => this._eventService.uiControlsUnmodified$.next(JSON.parse(JSON.stringify(uiControl)))),
              switchMap(uiControl => this._vermittlerdatenService.handleVermittlerData(uiControl).pipe(
                  map(uiControlMitVermittlerData => {
                    if (!!uiControlMitVermittlerData) {
                      return uiControlMitVermittlerData;
                    } else {
                      return uiControl;
                    }
                  })
              )),
              // Todo: muß weg wenn in fisalis auf OHNE_EINSCHRAENKUNGEN umgestellt
              tap((uiControl: UiControl) => {
                flatMap(uiControl.uiVorgang.UIArtefakt).forEach((artefakt: UiBaseControl) => {
                  if (
                    artefakt.FeldnameExtern === '/PortfoliovorlageID') {
                    artefakt.Darstellungsart = UIDarstellungsart.OHNE_EINSCHRAENKUNGEN;
                    //artefakt.WertEingabe = 88;
                    //console.log(artefakt);
                  }
                  if (artefakt.FeldnameExtern === '/PortfoliovorlageZuzahlungID') {
                    artefakt.Darstellungsart = UIDarstellungsart.OHNE_EINSCHRAENKUNGEN;
                    //artefakt.WertEingabe = 89;
                    //console.log(artefakt);
                  }
                });
              }),

              map((uiControl: UiControl) => {
                if (!!this._shellConfigService.shellParameter?.variantenvergleich) {
                  const excludeMap = {};
                  excludeMap['UIContainerComponent'] = ['Variantenvergleich'];
                  flatMap(uiControl.uiVorgang.UIArtefakt, excludeMap).forEach((artefakt: UiBaseControl) => {
                    if (!!artefakt.Darstellungsart) {
                      artefakt.Darstellungsart = UIDarstellungsart.AUSGEBLENDET;
                    }
                    if (artefakt.UIArtefakttyp === UIArtefaktTyp.ARTEFAKT_GRUPPE) {
                      artefakt['Hervorhebungstyp'] = void 0;
                    }
                  });
                }
                return uiControl;
              }),
              map((uiControl: UiControl) => this._modifyResponse(uiControl)),
              tap((uiControl: UiControl) => {
                this._eventService.uiControls$.next(uiControl);
                this._eventService.uiControlsNewst$.next(uiControl);
                this._eventService.uiControlsRequestPending$.next(false);
                this._eventService.angebotserstellungMoeglichJN$.next(uiControl.uiVorgang.AngebotserstellungMoeglichJN);
                this._vermittlerdatenService.checkAvvRelevanteArtefakte(uiControl);
              }),
              filter(_ => this._secondInitialRequest),
              switchMap(uiControl => this.requestUiControl(uiControl).pipe(tap(_ => this._secondInitialRequest = false))),
              catchError((error) => {
                this._secondInitialRequest = false;
                let errorUiControl;
                if (!!error.error && typeof (error.error) === 'string') {
                  this._errorService.pushErrorMessage(error.error);
                  errorUiControl = createRequestFailureUiControl(error.error);
                } else {
                  this._errorService.pushErrorMessage(error.message);
                  errorUiControl = createRequestFailureUiControl(error.message);
                }
                this._eventService.uiControls$.next(errorUiControl);
                this._eventService.uiControlsNewst$.next(errorUiControl);
                this._eventService.uiControlsUnmodified$.next(null);
                this._eventService.uiControlsRequestPending$.next(false);
                return of(null);
              }));
        })
    ).subscribe();
  }

  public requestUiControl(body?: any): Observable<boolean> {
    return this._eventService.urlParams$.pipe(
        take(1),
        map(params => {
          body = {
            ...body,
            kollektivId: !!body.kollektivId ? body.kollektivId : (!!params.kollektivId ? Number(params.kollektivId) : null),
            antragsgenerationDatum: body?.antragsgenerationDatum ? body.antragsgenerationDatum : this._momentService.moment(new Date()).format('YYYY-MM-DD')
          };
          if (this._uiConfigService.settings.environment.name !== 'PRODUKTION' && this._shellConfigService.getParameter().showEntwicklerleiste) {
            body = {
              ...body,
              kollektivId: !!params.kollektivId ? Number(params.kollektivId) : null,
              antragsgenerationDatum: params.antragsgenerationDatum ? params.antragsgenerationDatum : body.antragsgenerationDatum,
              ...{
                testControl: {
                  smsDoi: params.smsDoi,
                  emailDoi: params.emailDoi,
                  bonitaet: params.bonitaet,
                  compliance: params.compliance
                },
              }
            };
          }
          if (!body.uiVorgang) {
            const uiVorgang = new UiVorgang();
            uiVorgang.UIVorgangID = this._shellConfigService.getParameter().uiVorgangId;
            body.uiVorgang = uiVorgang;
          }
          if (params.processId) {
            body.processId = params.processId;
          }
          if (params.produktId) {
            body.produktId = params.produktId;
          }
          if (!!this._shellConfigService.shellParameter?.variantenvergleichData) {
            if (!!this._shellConfigService.shellParameter?.variantenvergleichData.versicherungsnummer &&
                !!this._shellConfigService.shellParameter?.variantenvergleichData.uiControl ||
                !this._shellConfigService.shellParameter?.variantenvergleichData.versicherungsnummer &&
                !this._shellConfigService.shellParameter?.variantenvergleichData.uiControl) {
              throw new Error('Aus dem GP-Portal bitte entweder die VNR oder das UiControl setzen.');
            }
            body.versicherungsnummer = this._shellConfigService.shellParameter?.variantenvergleichData.versicherungsnummer;
            body.produktId = this._shellConfigService.shellParameter?.variantenvergleichData.produktId ?
                this._shellConfigService.shellParameter?.variantenvergleichData.produktId :
                this._shellConfigService.shellParameter?.variantenvergleichData.uiControl?.produktId;
            if (this._shellConfigService.shellParameter?.variantenvergleichData.uiControl?.uiVorgang) {
              body.uiVorgang = this._shellConfigService.shellParameter?.variantenvergleichData.uiControl?.uiVorgang;
            }
            this._shellConfigService.shellParameter.variantenvergleichData = void 0;
          }
          if (!!this._shellConfigService.shellParameter?.variantenvergleich) {
            body = {...body, ...{variantenvergleich: true}};
            this._shellConfigService.shellParameter.uiLayout = UILayout.ALLE_SEITEN;
          }
          if (!body.produktId && !body.versicherungsnummer && !!this._shellConfigService.getParameter().produktId &&
              this._shellConfigService.getParameter().runtimeContext === RuntimeContext.CMS) {
            body.produktId = this._shellConfigService.getParameter().produktId;
          }
          if (!body.produktId && !body.versicherungsnummer && !!this._uiConfigService.settings.default_produkt &&
              this._shellConfigService.getParameter().runtimeContext === RuntimeContext.STANDALONE) {
            this._eventService.urlParams$.next({
              produktId: this._uiConfigService.settings.default_produkt,
              uiVorgangId: body.uiVorgangId,
            } as AntragsannahmeUrlParams);
            return false;
          }
          if (body.produktId || body.versicherungsnummer) {
            this._authService.requireKeycloak().then(() => {
              this._uiControlRequests$.next(body);
            });
            return true;
          } else {
            if (this._uiConfigService.settings.environment.name === 'Develop' &&
                this._secondInitialRequest === true) {
              throw new Error('Es muss mindestens einer der ' +
                  'Query-Parameter "produktId" oder "versicherungsnummer" gesetzt sein!');
            }
            return false;
          }
        }));
  }

  reloadUiControl(artefakt?: UIArtefakt) {
    this._eventService.uiControls$.pipe(take(1)).subscribe(control => {
      if (!!artefakt) {
        syncSameArtifacts(control, artefakt);
      }
      this._eventService.uiControls$.next(control);
      this._eventService.uiControlsNewst$.next(control);
      this._eventService.uiControlsUnmodified$.next(control);
    });
  }

  private _modifyResponse(uiControl: UiControl) {
    if (this._shellConfigService.getParameter().runtimeContext === RuntimeContext.SB_SHELL) {
      flatMap(uiControl.uiVorgang.UIArtefakt).forEach((artefakt: UiBaseControl) => {
        if (artefakt.FeldnameRisikopruefung?.indexOf('versicherungsbeginn') >= 0) {
          const datumArtefakt = artefakt as DateControl;
          datumArtefakt.UIArtefakttyp = UIArtefaktTyp.DATE;
          datumArtefakt.DatumComponent = 'Normal';
        }
      });
    }
    return uiControl;
  }
}
